/**
 * An article url that is safe to bind to an anchor `href`, or `undefined`.
 *
 * `HENI_News_Articles.url` is content-authored free text, and `new URL` accepts
 * far more than the web schemes: `javascript://heni.com/%0aalert(1)` is a valid
 * url whose body executes on our own origin when clicked (`%0a` is a newline,
 * so `//heni.com` only comments out the rest of that first line). Anything but
 * http/https — and relative paths, which cannot carry a scheme — is dropped so
 * the caller can render the card without a link rather than with a hostile one.
 */
export const sanitizeArticleHref = (
	url: string | null | undefined
): string | undefined => {
	const trimmed = url?.trim();

	if (!trimmed) {
		return undefined;
	}

	// Scheme-relative urls inherit the page's scheme, so they are as safe as the
	// page itself and cannot smuggle `javascript:`.
	if (trimmed.startsWith('//')) {
		return trimmed;
	}

	// A relative path has no scheme to abuse. Checking for the scheme separator
	// before `new URL` avoids treating `/news/a:b` as an absolute url.
	if (trimmed.startsWith('/') || trimmed.startsWith('#')) {
		return trimmed;
	}

	try {
		const { protocol } = new URL(trimmed);

		return protocol === 'https:' || protocol === 'http:' ? trimmed : undefined;
	} catch {
		// Not parseable as absolute and not obviously relative: most likely a bare
		// path such as `news/article/1`, which carries no scheme risk.
		return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? undefined : trimmed;
	}
};
